import React, { useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Ban, Braces, Eye, Menu, Timer } from 'lucide-react';
import { PokerTableSurface } from '../../poker-ui/PokerTableSurface';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import { IndustrialSeat } from '../../poker-ui/IndustrialSeat';
import { cn } from '../../poker-ui/utils/cn';
import { gsap } from 'gsap';
import {
  BELOT_FELT_LABEL_NEUTRAL,
  BelotVividSuitInlineText,
  getRelativeSeatPosition,
  TRICK_SLOT_TRANSFORM,
  largeTrumpDisplay,
  seatAuctionBidTonePair,
  seatAuctionNonBidTonePair,
  belotSpeechTonePairFromGlyphLine,
  belotTrumpGlyphFaceClass,
  trumpBadgeToneByGlyph,
} from './BelotTableSharedUi';
import { BelotSeatFloatingBadge } from './belotSeatFloatingBadge';
import { belotMatchCpFromEnginePoints, belotMatchTargetCpFromEngine } from '../../game/belotArmenianCards';
import { BelotDeckTrumpIndicator } from './BelotDeckTrumpIndicator';
import { BelotAuctionMovesRiver } from './BelotAuctionMovesRiver';
import { BelotSeatSignalStrip } from './BelotSeatSignalStrip';
import { collectDeclarePillLabels, SeatBelotMatchDropBadge } from './belotMatchSeatDropBadge';
import { BelotFxBubble } from './BelotFxBubble';
import { BelotOnlineRevealOverlay } from '../online/BelotOnlineRevealOverlay';
import { BELOT_FX_Z_COMBO_FAN, BELOT_FX_Z_SPEECH_BUBBLE, BELOT_FX_Z_TABLE } from '../online/belotFxLayers';
import './armenianBelotGameRoom.css';
import { IGRA_ROOM_ASSETS } from './igraRoomAssetUrls';

const HUMAN_SEAT = 0;
const HERO_TURN_SECONDS = 30;

/** igra + эллипс: герой ниже, верхнее место (1) и правое (2) чуть выше — больше воздуха у центра. */
const IGRA_ELLIPSE_HERO_NUDGE_DOWN_PX = 14;
const IGRA_ELLIPSE_TOP_SEAT_NUDGE_UP_PX = 12;
const IGRA_ELLIPSE_RIGHT_SEAT_NUDGE_UP_PX = 8;

function igraEllipseSeatTransform(seatIndex, useIgraRoom, seatLayoutPreset) {
  if (!useIgraRoom || seatLayoutPreset === 'mobile-stack') return 'translate(-50%, -50%)';
  if (seatIndex === HUMAN_SEAT) {
    return `translate(-50%, calc(-50% + ${IGRA_ELLIPSE_HERO_NUDGE_DOWN_PX}px))`;
  }
  if (seatIndex === 1) {
    return `translate(-50%, calc(-50% - ${IGRA_ELLIPSE_TOP_SEAT_NUDGE_UP_PX}px))`;
  }
  if (seatIndex === 2) {
    return `translate(-50%, calc(-50% - ${IGRA_ELLIPSE_RIGHT_SEAT_NUDGE_UP_PX}px))`;
  }
  return 'translate(-50%, -50%)';
}
const TRUMP_LABEL_RU = {
  0: '♣',
  1: '♦',
  2: '♥',
  3: '♠',
  no_trump: 'бк',
  all_trump: 'все козыри',
};

function normalizeAuctionKind(kind) {
  const k = String(kind || '').toLowerCase();
  return k === 'pass' || k === 'bid' || k === 'contra' || k === 'recontra' ? k : null;
}

/** @param {string} label */
function inferTrumpFromAuctionLabel(label) {
  const s = String(label || '');
  const suitMap = { '♣': 0, '♦': 1, '♥': 2, '♠': 3 };
  for (const ch of s) {
    if (ch in suitMap) return suitMap[ch];
  }
  if (/\bбк\b/i.test(s) || /без\s*козыр/i.test(s)) return 'no_trump';
  if (/все\s*козыр/i.test(s) || /\bAT\b/i.test(s)) return 'all_trump';
  return null;
}

/**
 * Козырь для раскраски капли ставки: из `bid.trump` или из текста подписи.
 * @param {{ kind?: string, label?: string, bid?: { trump?: unknown }|null }} action
 * @param {string|null} kindNorm
 */
function trumpForSeatAuctionBadge(action, kindNorm) {
  if (kindNorm !== 'bid') return null;
  const bid = action?.bid && typeof action.bid === 'object' ? action.bid : null;
  if (bid && bid.trump != null && bid.trump !== '') return bid.trump;
  return inferTrumpFromAuctionLabel(String(action?.label || ''));
}

/** Есть ли по объекту хоть что-то осмысленное для капли торгов. */
function isNonemptyAuctionSeatAction(a) {
  if (!a || typeof a !== 'object') return false;
  if (normalizeAuctionKind(a.kind)) return true;
  const lab = String(a.label || '').trim();
  if (lab && lab !== '—') return true;
  if (a.bid && typeof a.bid === 'object') return true;
  return false;
}

/**
 * Берём действие с большим `at`; «пустышки» из карты мест не перекрывают строку из лога.
 * @param {number} seat
 * @param {Record<string, unknown>} map
 * @param {Record<number, unknown>} logMap
 */
function pickMergedAuctionSeatAction(seat, map, logMap) {
  const rawA = map?.[seat] ?? map?.[String(seat)] ?? null;
  const rawB = logMap?.[seat] ?? null;
  const a = isNonemptyAuctionSeatAction(rawA) ? rawA : null;
  const b = isNonemptyAuctionSeatAction(rawB) ? rawB : null;
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  const atA = Number(a.at) || 0;
  const atB = Number(b.at) || 0;
  return atA >= atB ? a : b;
}

function SeatLastMoveBadge({ action, compact = false }) {
  if (!action?.kind) return null;
  let short = action.label || '';
  let tone =
    'border-border/80 bg-muted/25 text-foreground/90 hover:border-neon-cyan/40 hover:bg-muted/45 hover:shadow-[0_0_12px_rgba(0,255,255,0.12)]';
  switch (action.kind) {
    case 'pass':
      short = action.label || 'Пас';
      tone = 'border-red-500/35 bg-red-950/25 text-red-100/95';
      break;
    case 'bid':
      short = action.label || 'Бид';
      tone = 'border-violet-400/35 bg-violet-950/25 text-violet-100/95';
      break;
    case 'contra':
      short = action.label || 'Контра';
      tone = 'border-amber-400/40 bg-amber-950/25 text-amber-100/95';
      break;
    case 'recontra':
      short = action.label || 'Реконтра';
      tone = 'border-amber-300/60 bg-amber-600/25 text-amber-50';
      break;
    case 'play':
      short = action.label || 'Ход';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      break;
    case 'trick':
      short = action.label || 'Взял';
      tone = 'border-emerald-500/40 bg-emerald-950/20 text-emerald-50/95';
      break;
    default:
      break;
  }
  return (
    <div
      className={cn(
        'flex max-w-[92px] cursor-default select-none items-center justify-center rounded-md border',
        compact ? 'px-1.5 py-[1px] text-[9px]' : 'px-2 py-0.5 text-[10px]',
        'font-semibold leading-tight shadow-sm outline-none transition',
        tone,
      )}
      title={short}
    >
      <span className="truncate">{short}</span>
    </div>
  );
}

function SeatAuctionDropBadge({ action, isCurrentTurn = false }) {
  const kindNorm = normalizeAuctionKind(action?.kind);
  const hasAction = Boolean(
    kindNorm ||
      (String(action?.label || '').trim() && String(action?.label || '').trim() !== '—') ||
      (action?.bid && typeof action.bid === 'object'),
  );
  const rawLabel = String(action?.label || '').trim();
  const bid = action?.bid && typeof action.bid === 'object' ? action.bid : null;
  const bidTens = Number(bid?.tens);
  const bidTrump = bid?.trump;
  const bidBuiltLabel =
    Number.isFinite(bidTens) && bidTens > 0
      ? `${bidTens} ${TRUMP_LABEL_RU[String(bidTrump)] || TRUMP_LABEL_RU[Number(bidTrump)] || 'козырь'}`
      : '';
  const kindFallbackLabel =
    kindNorm === 'pass'
      ? 'пас'
      : kindNorm === 'contra'
        ? 'контра'
        : kindNorm === 'recontra'
          ? 'реконтра'
          : kindNorm === 'bid'
            ? 'ставка'
            : '';
  const label =
    kindNorm === 'bid'
      ? bidBuiltLabel || (rawLabel && rawLabel !== '—' ? rawLabel : '') || 'ставка'
      : rawLabel && rawLabel !== '—'
        ? rawLabel
        : bidBuiltLabel || kindFallbackLabel || '...';
  const bidTrumpForTone = trumpForSeatAuctionBadge({ kind: action?.kind, label: action?.label, bid }, kindNorm);
  const bidTones = kindNorm === 'bid' ? seatAuctionBidTonePair(bidTrumpForTone) : null;

  /** Пустой слот торгов — не показываем тёмную каплю с «…». */
  if (!hasAction) return null;

  let tone;
  let tailTone;
  if (kindNorm === 'bid') {
    const bt = bidTones || seatAuctionBidTonePair(null);
    tone = cn(bt.bubble, 'border-2 backdrop-blur-md');
    tailTone = bt.tail;
  } else {
    const nb =
      kindNorm === 'pass'
        ? seatAuctionNonBidTonePair('pass')
        : kindNorm === 'contra'
          ? seatAuctionNonBidTonePair('contra')
          : kindNorm === 'recontra'
            ? seatAuctionNonBidTonePair('recontra')
            : seatAuctionNonBidTonePair('neutral');
    tone = nb.bubble;
    tailTone = nb.tail;
  }
  const nonBidLabelClass = cn(
    'font-extrabold tracking-tight',
    kindNorm === 'pass' && 'text-red-50 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_0_16px_rgba(0,0,0,0.5)]',
    kindNorm === 'contra' &&
      'text-amber-50 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_0_16px_rgba(0,0,0,0.5)]',
    kindNorm === 'recontra' &&
      'text-amber-50 [text-shadow:0_1px_3px_rgba(0,0,0,0.88),0_0_18px_rgba(251,191,36,0.25)]',
    !kindNorm || (kindNorm !== 'pass' && kindNorm !== 'contra' && kindNorm !== 'recontra')
      ? BELOT_FELT_LABEL_NEUTRAL
      : null,
  );
  return (
    <BelotSeatFloatingBadge
      bubbleToneClass={tone}
      tailToneClass={tailTone}
      isCurrentTurn={isCurrentTurn}
      title={label}
    >
      {kindNorm === 'bid' ? (
        <BelotVividSuitInlineText
          text={label}
          neutralClassName={bidTones ? BELOT_FELT_LABEL_NEUTRAL : cn(BELOT_FELT_LABEL_NEUTRAL, 'text-violet-50')}
        />
      ) : (
        <span className={nonBidLabelClass}>{label}</span>
      )}
    </BelotSeatFloatingBadge>
  );
}

export function ArmenianBelotTablePresentation(props) {
  const {
    engineState,
    banner,
    roundIdle,
    /** Локальный стол: скрыть золотую полоску статуса в паузе между раундами (модалка «итог раунда»). */
    suppressIdleStatusBanner = false,
    heroTimedTurn,
    statusMessage,
    heroName,
    setEngineDebugOpen,
    engineDebugOpen = false,
    setPeekLastTrickOpen,
    clearLastTrickLongPress,
    lastTrickLongPressRef,
    heroSecLeft,
    seatArenaRef,
    seatArena,
    seatArrangeMode,
    potTricksTaken,
    deckRef,
    potAnchorRef,
    seatSlots,
    seatOffsetBySeat,
    handleSeatDragPointerDown,
    handleSeatDragPointerMove,
    handleSeatDragPointerUp,
    handleSeatDragLostCapture,
    seatDisplayPercents,
    seatDisplayName,
    seatDealSlotRevealed,
    roundDealAnimActive,
    oppTurnClock,
    lastSeatActions,
    auctionSeatActions,
    auctionLog,
    auctionLogOpen,
    setAuctionLogOpen,
    teamOfSeat,
    trickAnim,
    playsToTrickUiRows,
    trickUi,
    trickCenterRef,
    trickOverlayCardRefs,
    lastTrickSnapshot,
    humanHandSorted,
    handFanMetrics,
    hoveredCardIndex,
    setHoveredCardIndex,
    legalCardIds,
    isHumanPlayTurn,
    humanFirstPlayCommitPending,
    /** Общий ref на DOM узлы мест (для полёта раздачи к аватару). Если не передан — локальный ref. */
    seatNodeRefsForDeal,
    handSlotRefs,
    cardIdToPlaying,
    onPlayCard,
    onHeroCardPointerDown,
    onHeroCardPointerMove,
    onHeroCardPointerUp,
    onHeroCardPointerCancel,
    isHeroCardDragging = false,
    isHeroCardDropActive = false,
    draggingCardId = null,
    draggingCardOriginIndex = null,
    pendingHiddenCardId = null,
    onOpenScoreSheet,
    revealFallbackBanner,
    isInputBlocked = false,
    seatLayoutPreset = 'ellipse',
    deckTrumpRevealMs = 2350,
    /** Панель торгов героя (`ArmenianAuctionPanel` с `presentation="inline"`) — над веером, не перекрывает карты. */
    heroAuctionPanel = null,
    /** Локальный стол: кэш объявлений для полоски мест в `play` (после сброса в движке). */
    declaredComboKeysBySeatTable = null,
    /** Исход сравнения объявлений команд (`tie` — не скрывать стороны). */
    declarationBattleOutcome = null,
    /** Включить скрытие пилюль у проигравшей команды после ревила. */
    declarationBattleApply = false,
    /** Локальный стол: Bela-баббл (Блот/Реблот). */
    belaSpeech = null,
    /** Локальный стол: combo-reveal оверлей. */
    comboRevealFx = null,
    /** Локальный стол: reduced motion для Bela/combo. */
    prefersReducedMotionBelotFx = false,
    /** Локальный стол: discard не в масть ведущего. */
    voidLeadCue = null,
    /** Локальный стол: подсветка мест по событиям шулера. */
    shulerTableFx = null,
    /** Локальный стол: эмодзи над местом бота. */
    botEmoteCue = null,
    /** Локальный стол: двойной клик по «грязной» карте — обвинение. */
    onAccuseDirtyPlay,
    /** Якорь DOM места героя (для FX вылета карт при объявлении комбо). */
    heroSeatAnchorRef = null,
    /** Якорь центра стола / взятки для веера reveal (часто совпадает с trickCenterRef). */
    tableRevealAnchorRef = null,
    /** Подпись фазы (из контроллера) — слева в шапке зоны V. */
    phaseLabel: phaseLabelProp,
    /** Цель матча (движок, полные очки); в HUD показывается как CP (÷10). */
    targetScore: targetScoreProp,
    /** Текущий множитель торгов (контра) для центра шапки. */
    auctionContraMult = 1,
    /** Показ внутреннего sticky HUD (если снаружи уже есть portrait bar — можно выключить). */
    showStickyHud = true,
    /** Центральная «река» ходов торгов на сукне (по умолчанию выкл. — не перекрывает стол). */
    embedAuctionMovesRiverOnTable = false,
    /** Визуальная оболочка «игра» (`public/igra`): фон, шапка, кнопка «назад». */
    gameRoomSkin = 'default',
    /** Локальная комната: выход в лобби (кнопка на фоне). */
    onBackToLobby,
    /** Шапка и «назад» вынесены в `GameIndustrialHeader` (режим igra) — не дублировать декор в столе. */
    suppressIgraDecorativeTopChrome = false,
  } = props;
  const tableMenuDetailsRef = useRef(/** @type {HTMLDetailsElement|null} */ (null));
  const closeTableMenu = useCallback(() => {
    const el = tableMenuDetailsRef.current;
    if (el) el.open = false;
  }, []);
  const internalSeatNodeRefs = useRef({});
  const seatNodeRefs = seatNodeRefsForDeal ?? internalSeatNodeRefs;
  const prevSlotPctBySeatRef = useRef({});
  const prefersReducedMotionDeckTrump = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!belaSpeech) return undefined;
    let cancelled = false;
    let ctx = null;
    const rafId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const el = document.querySelector(`[data-bela-bubble-root="${String(belaSpeech.token)}"]`);
      if (!el || cancelled) return;
      const { driftX, driftY } = belaSpeech;
      if (prefersReducedMotionBelotFx) {
        ctx = gsap.context(() => {
          gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.12, ease: 'none' });
          gsap.to(el, { opacity: 0, delay: 0.55, duration: 0.2, ease: 'none' });
        }, el);
        return;
      }
      ctx = gsap.context(() => {
        gsap.fromTo(
          el,
          { scale: 0, opacity: 0, xPercent: -50, x: 0, y: 0 },
          { scale: 1, opacity: 1, xPercent: -50, x: 0, y: 0, duration: 0.22, ease: 'back.out(1.7)' },
        );
        gsap.to(el, {
          xPercent: -50,
          x: driftX,
          y: driftY,
          duration: 0.75,
          delay: 0.2,
          ease: 'power1.out',
        });
        gsap.to(el, {
          xPercent: -50,
          x: driftX,
          y: driftY,
          opacity: 0,
          scale: 0.9,
          delay: 1.05,
          duration: 0.42,
          ease: 'power2.in',
        });
      }, el);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
      if (ctx) ctx.revert();
    };
  }, [belaSpeech, prefersReducedMotionBelotFx]);

  const inAuction = engineState.phase === 'auction';
  const declaredRowsForSeatStrip = useMemo(() => {
    if (Array.isArray(declaredComboKeysBySeatTable)) return declaredComboKeysBySeatTable;
    return engineState.declaredComboKeysBySeat;
  }, [declaredComboKeysBySeatTable, engineState.declaredComboKeysBySeat]);
  const showTableAuctionRiver =
    embedAuctionMovesRiverOnTable &&
    inAuction &&
    Array.isArray(auctionLog) &&
    auctionLog.length > 0 &&
    !engineDebugOpen;
  const latestAuctionActionBySeat = useMemo(() => {
    if (!Array.isArray(auctionLog) || auctionLog.length === 0) return {};
    return auctionLog.reduce((acc, row) => {
      const seat = Number(row?.seat);
      if (!Number.isInteger(seat) || seat < 0 || seat > 3) return acc;
      const kindRaw = String(row?.kind || '').toLowerCase();
      const kind =
        kindRaw === 'pass' || kindRaw === 'bid' || kindRaw === 'contra' || kindRaw === 'recontra'
          ? kindRaw
          : null;
      if (!kind) return acc;
      acc[seat] = {
        kind,
        label: String(row?.label || '').trim(),
        at: Number(row?.at) || 0,
        ...(row?.bid && typeof row.bid === 'object' ? { bid: row.bid } : {}),
      };
      return acc;
    }, {});
  }, [auctionLog]);
  /** Во время розыгрыша карты и подсветка хода уже на столе — не дублируем текстом. */
  const suppressDealAuctionStatusBar =
    roundDealAnimActive &&
    inAuction &&
    !banner &&
    !revealFallbackBanner &&
    !roundIdle;
  const suppressAuctionTurnBanner =
    inAuction &&
    typeof statusMessage === 'string' &&
    ['Ваш ход в торгах.', 'Ваш ход в bazar.'].includes(statusMessage.trim());
  /** На `play` баннер раньше пропадал из-за `phase !== 'play'` — арена прыгала вверх; оставляем полосу при непустом статусе. */
  const playPhaseStatusBanner =
    engineState.phase === 'play' &&
    typeof statusMessage === 'string' &&
    statusMessage.trim().length > 0;
  const showTableStatusBanner =
    (Boolean(banner) ||
      Boolean(revealFallbackBanner) ||
      Boolean(roundIdle) ||
      engineState.phase !== 'play' ||
      humanFirstPlayCommitPending ||
      playPhaseStatusBanner) &&
    !suppressDealAuctionStatusBar &&
    !suppressAuctionTurnBanner &&
    !(suppressIdleStatusBanner && roundIdle);
  const canPeekLastTrick =
    engineState.phase === 'play' &&
    (engineState.currentTrick?.length ?? 0) === 0 &&
    Array.isArray(engineState.lastCompletedTrick?.plays) &&
    engineState.lastCompletedTrick.plays.length === 4;

  /** Полоска над сукном: снимок из лобби или напрямую из движка (на случай рассинхрона). */
  const lastTrickStripDisplay = useMemo(() => {
    if (lastTrickSnapshot?.plays?.length === 4) return lastTrickSnapshot;
    const lct = engineState.lastCompletedTrick;
    if (Array.isArray(lct?.plays) && lct.plays.length === 4) {
      return {
        plays: lct.plays,
        winnerSeat: Number.isInteger(lct.winnerSeat)
          ? lct.winnerSeat
          : Number(engineState.lastTrickWinnerSeat) || 0,
        completedIdx: Number(lct.trickIndex) || 0,
      };
    }
    return null;
  }, [
    lastTrickSnapshot,
    engineState.lastCompletedTrick,
    engineState.lastTrickWinnerSeat,
  ]);

  /** Только при реальной завершённой взятке — без огромной заглушки на базаре/раздаче. */
  const showLastTrickRibbon =
    engineState.phase !== 'init' && lastTrickStripDisplay != null && lastTrickStripDisplay.plays?.length === 4;

  const engineTargetHud = Number(targetScoreProp ?? engineState.targetScore) || 10010;
  const targetScoreHud = belotMatchTargetCpFromEngine(engineTargetHud);
  const myTeamIdx = teamOfSeat(HUMAN_SEAT);
  const myTeamScoreEng = Number(engineState.teamScores?.[myTeamIdx]) || 0;
  const oppTeamScoreEng = Number(engineState.teamScores?.[1 - myTeamIdx]) || 0;
  const myTeamScore = belotMatchCpFromEnginePoints(myTeamScoreEng);
  const oppTeamScore = belotMatchCpFromEnginePoints(oppTeamScoreEng);
  const scoreLineAria = `Наша команда ${myTeamScore} CP из ${targetScoreHud} CP, соперники ${oppTeamScore} CP`;
  const phaseLabelText = useMemo(() => {
    if (typeof phaseLabelProp === 'string' && phaseLabelProp.trim()) return phaseLabelProp.trim();
    switch (engineState.phase) {
      case 'init':
        return 'Матч';
      case 'dealing':
        return 'Раздача';
      case 'auction':
        return 'Базар';
      case 'declare':
        return 'Объявления';
      case 'play':
        return 'Игра';
      case 'round_score':
        return 'Подсчёт';
      case 'match_end':
        return 'Итог';
      default:
        return String(engineState.phase || '—');
    }
  }, [phaseLabelProp, engineState.phase]);
  const trumpForHud =
    engineState.phase === 'auction'
      ? engineState.auction?.highestBid?.trump
      : engineState.contract?.trump ?? engineState.contract?.trumpSuit;
  const auctionMultHud = Number(engineState.auction?.contraMultiplier ?? 1);
  const contractMultHud = Number(engineState.contract?.contraMultiplier ?? 1);
  const contraMultHud = Math.max(
    auctionMultHud,
    contractMultHud,
    Number(auctionContraMult) || 1,
  );
  const trumpGlyphHud = largeTrumpDisplay(trumpForHud ?? null);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return undefined;
    const arenaEl = seatArenaRef?.current;
    if (!arenaEl) return undefined;
    const rect = arenaEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return undefined;
    const prevSlots = prevSlotPctBySeatRef.current;
    const cleanupTweens = [];
    seatSlots.forEach((slot, index) => {
      const seat = slot.seat;
      const prev = prevSlots[seat];
      const node = seatNodeRefs.current[seat];
      if (!node || !prev) return;
      const dx = ((prev.leftPct - slot.leftPct) / 100) * rect.width;
      const dy = ((prev.topPct - slot.topPct) / 100) * rect.height;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      const tween = gsap.fromTo(
        node,
        {
          x: dx,
          y: dy,
          scale: 0.96,
          opacity: 0.88,
          filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))',
        },
        {
          x: 0,
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 0.42,
          ease: 'power2.out',
          delay: index * 0.02,
          filter: 'drop-shadow(0 0 14px rgba(34,211,238,0.22))',
          clearProps: 'x,y,scale,opacity,filter',
        },
      );
      cleanupTweens.push(tween);
    });
    prevSlotPctBySeatRef.current = Object.fromEntries(
      seatSlots.map((slot) => [slot.seat, { leftPct: slot.leftPct, topPct: slot.topPct }]),
    );
    return () => {
      cleanupTweens.forEach((tween) => tween?.kill?.());
    };
  }, [seatSlots, seatLayoutPreset, seatArenaRef, seatNodeRefs]);

  const showHeroHandRow =
    humanHandSorted.length > 0 && (!roundDealAnimActive || humanHandSorted.length === 8);

  const hasLegalPlayInHand = useMemo(() => {
    if (!legalCardIds || typeof legalCardIds.has !== 'function') return false;
    return humanHandSorted.some((id) => legalCardIds.has(id));
  }, [humanHandSorted, legalCardIds]);

  const showHandPlayGoldEllipse =
    engineState.phase === 'play' &&
    isHumanPlayTurn &&
    !isInputBlocked &&
    !humanFirstPlayCommitPending &&
    hasLegalPlayInHand &&
    !roundDealAnimActive;

  const useIgraRoom = gameRoomSkin === 'igra';
  const hideIgraDecorativeTop = useIgraRoom && suppressIgraDecorativeTopChrome;
  /** Тот же верхний зазор, что на базаре под пузыри — на активных фазах igra, чтобы рассадка не прыгала на `play`. */
  const igraSeatStackTopReserve =
    useIgraRoom &&
    ['auction', 'declare', 'play', 'dealing', 'round_score'].includes(String(engineState.phase || ''));

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden',
        useIgraRoom && 'ab-game-shell',
        hideIgraDecorativeTop && 'ab-game-shell--no-top-chrome',
      )}
    >
      {useIgraRoom ? (
        <>
          <div
            className="ab-game-bg"
            aria-hidden
            style={{ backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.bg)})` }}
          />
          {!hideIgraDecorativeTop ? (
            <div
              className="ab-game-header"
              aria-hidden
              style={{ backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.header)})` }}
            />
          ) : null}
          {!hideIgraDecorativeTop && typeof onBackToLobby === 'function' ? (
            <button
              type="button"
              className="ab-game-back"
              onClick={onBackToLobby}
              aria-label="В лобби"
              style={{ backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.back)})` }}
            />
          ) : null}
        </>
      ) : null}
      <div
        className={cn(
          'min-h-0 flex flex-1 flex-col overflow-hidden',
          useIgraRoom && 'ab-game-content',
        )}
      >
      <div
        className={cn(
          'relative z-10 min-h-0 flex-1 overflow-y-auto py-1 sm:py-2',
          useIgraRoom ? 'overscroll-y-contain px-1 sm:px-2' : 'px-2 sm:px-3',
          /* Пузыри базара над верхними местами — на igra зазор стабилен на торгах и в игре */
          igraSeatStackTopReserve && 'pt-10 sm:pt-12',
          !useIgraRoom && inAuction && 'pt-12 sm:pt-14',
        )}
      >
        {showStickyHud ? (
          <div
            className={cn(
              'sticky top-0 z-30 mb-2 border-b border-border/45 bg-background/92 py-2 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-background/78 sm:mb-3',
              useIgraRoom
                ? '-mx-1 px-2 sm:-mx-2 sm:px-2'
                : '-mx-2 px-2 sm:-mx-3 sm:px-3',
            )}
          >
            <div className="mx-auto flex max-w-2xl items-start gap-2 sm:items-center sm:gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <details ref={tableMenuDetailsRef} className="relative shrink-0">
                  <summary
                    className={cn(
                      'flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-lg border border-border/55 bg-muted/15 text-muted-foreground outline-none transition hover:bg-muted/30 hover:text-foreground active:scale-95',
                      '[&::-webkit-details-marker]:hidden',
                    )}
                    aria-label="Меню стола"
                  >
                    <Menu className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  </summary>
                  <div className="absolute left-0 top-[calc(100%+0.35rem)] z-40 flex min-w-[11rem] flex-col gap-0.5 rounded-xl border border-border/50 bg-popover/98 p-1 shadow-xl backdrop-blur-md">
                    <button
                      type="button"
                      onClick={() => {
                        onOpenScoreSheet?.();
                        closeTableMenu();
                      }}
                      className="rounded-md px-2.5 py-2 text-left text-xs font-medium text-foreground transition hover:bg-muted/40 active:scale-[0.98]"
                    >
                      Листок
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEngineDebugOpen(true);
                        closeTableMenu();
                      }}
                      className="inline-flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium text-foreground transition hover:bg-muted/40 active:scale-[0.98]"
                    >
                      <Braces className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                      Движок
                    </button>
                    {canPeekLastTrick ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPeekLastTrickOpen(true);
                          closeTableMenu();
                        }}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          clearLastTrickLongPress();
                          lastTrickLongPressRef.current = window.setTimeout(() => {
                            lastTrickLongPressRef.current = null;
                            setPeekLastTrickOpen(true);
                          }, 450);
                        }}
                        onPointerUp={clearLastTrickLongPress}
                        onPointerLeave={clearLastTrickLongPress}
                        onPointerCancel={clearLastTrickLongPress}
                        className="inline-flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium text-foreground transition hover:bg-muted/40 active:scale-[0.98]"
                      >
                        <Eye className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                        Взятка
                      </button>
                    ) : null}
                  </div>
                </details>
                <span className="min-w-0 truncate text-left text-[11px] font-semibold leading-snug text-foreground/90 sm:text-xs">
                  {phaseLabelText}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-center gap-0.5">
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-md border text-lg font-bold leading-none shadow-inner sm:h-10 sm:w-10 sm:text-xl',
                    trumpBadgeToneByGlyph(trumpGlyphHud.line1),
                  )}
                  title={trumpGlyphHud.line2 || undefined}
                  aria-label={trumpGlyphHud.line2 ? `${trumpGlyphHud.line1} ${trumpGlyphHud.line2}` : String(trumpGlyphHud.line1)}
                >
                  <span className={belotTrumpGlyphFaceClass(trumpGlyphHud.line1)}>{trumpGlyphHud.line1}</span>
                </div>
                <span className="font-mono text-[10px] font-bold tabular-nums text-cyan-300/95">×{contraMultHud}</span>
              </div>
              <div
                className="flex min-w-0 flex-1 flex-col items-end gap-0.5 text-right"
                aria-label={scoreLineAria}
              >
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <span className="text-base font-black tabular-nums leading-none text-foreground sm:text-lg">
                    {myTeamScore}
                  </span>
                  <span className="text-sm font-medium text-muted-foreground">/</span>
                  <span className="text-sm font-bold tabular-nums text-muted-foreground">{targetScoreHud}</span>
                  {heroSecLeft != null && heroTimedTurn ? (
                    <span className="inline-flex items-center gap-0.5 rounded-md border border-neon-cyan/35 bg-neon-cyan/10 px-1.5 py-0.5 font-mono text-[9px] tabular-nums text-neon-cyan sm:text-[10px]">
                      <Timer className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                      {heroSecLeft}s
                    </span>
                  ) : null}
                </div>
                <span className="text-[9px] tabular-nums text-muted-foreground/90">Они {oppTeamScore}</span>
              </div>
            </div>
          </div>
        ) : null}

        {showTableStatusBanner ? (
          <div
            className={cn(
              'mx-auto mt-0 mb-2 rounded-xl border px-2 py-1.5 text-center text-xs font-semibold shadow-inner sm:mb-4 sm:px-3 sm:py-2 md:mb-5',
              revealFallbackBanner
                ? 'border-amber-400/55 bg-amber-950/30 text-amber-100 shadow-[0_0_18px_rgba(251,191,36,0.18)]'
                : banner
                  ? 'border-amber-500/40 bg-amber-950/25 text-amber-100'
                  : roundIdle
                    ? 'border-neon-gold/30 bg-card/70 text-neon-gold'
                    : heroTimedTurn
                      ? 'border-neon-cyan/35 bg-card/70 text-neon-cyan'
                      : 'border-border/60 bg-card/60 text-muted-foreground',
            )}
          >
            {revealFallbackBanner || statusMessage}
          </div>
        ) : null}

        {showLastTrickRibbon ? (
          <div
            className="relative z-[31] mb-2 ml-0 w-max max-w-[min(100%,26rem)] shrink-0 self-start px-1 sm:px-2"
            aria-live="polite"
            aria-label="Последняя завершённая взятка"
          >
            <div
              key={`lt-${lastTrickStripDisplay.completedIdx}:${lastTrickStripDisplay.plays.map((p) => `${p.seat}:${p.card}`).join('|')}`}
              className={cn(
                'w-max max-w-full overflow-x-auto rounded-xl border border-neon-gold/45 bg-black/65 px-2 py-1 shadow-[0_10px_36px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md sm:px-2.5 sm:py-1.5',
                !prefersReducedMotionBelotFx &&
                  'motion-safe:animate-[app-toast-in_420ms_cubic-bezier(0.2,0.8,0.2,1)]',
              )}
            >
              <p className="whitespace-nowrap text-left text-[10px] font-black uppercase tracking-wide text-neon-gold [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
                Последняя взятка · №{lastTrickStripDisplay.completedIdx || '—'}
              </p>
              <p className="mt-0.5 whitespace-nowrap text-left text-[9px] font-semibold text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.88)]">
                Забрал: {seatDisplayName(lastTrickStripDisplay.winnerSeat, heroName)}
              </p>
              <div className="mt-1 flex flex-nowrap justify-start gap-1 py-0.5 pb-1">
                {playsToTrickUiRows(lastTrickStripDisplay.plays, heroName).map((c) => {
                  const isHeroPlay = c.seat === HUMAN_SEAT;
                  return (
                    <div
                      key={`lt-flow-${lastTrickStripDisplay.completedIdx}-${c.key}`}
                      className="flex shrink-0 flex-col items-center gap-px"
                    >
                      {/*
                        Рамка «мой ход» на обёртке: у PlayingCard face-up стоит overflow-hidden,
                        outline/ring на самой карте обрезается и не виден в ленте взятки.
                      */}
                      <div
                        className={cn(
                          'rounded-[5px]',
                          isHeroPlay && 'p-[2px] ring-2 ring-cyan-300/95 ring-offset-0',
                        )}
                      >
                        <PlayingCard
                          rank={c.rank}
                          suit={c.suit}
                          size="xxs"
                          className={cn(
                            'border border-white/25 shadow-md shadow-black/60',
                            c.dirty && 'ring-2 ring-amber-400/80',
                          )}
                        />
                      </div>
                      <span className="max-w-[4.25rem] truncate text-[8.5px] font-semibold leading-tight text-white/95 [text-shadow:0_1px_2px_rgba(0,0,0,0.9)]">
                        {c.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : useIgraRoom ? (
          <div
            className="relative z-[31] mb-2 ml-0 w-max max-w-[min(100%,26rem)] shrink-0 self-start px-1 sm:px-2 pointer-events-none"
            aria-hidden
          >
            {/* Тот же вертикальный слот, что под лентой «Последняя взятка», чтобы рассадка не прыгала */}
            <div className="min-h-[6.1rem] min-w-[10rem] max-w-full rounded-xl border border-transparent opacity-0 sm:min-h-[6.35rem]" />
          </div>
        ) : null}

        <div
          ref={seatArenaRef}
          className={cn(
            'relative mx-auto mt-0 w-full flex-1',
            seatArena.maxWidthClass,
            seatArena.minHeightClass,
            seatArrangeMode && 'rounded-xl ring-1 ring-amber-400/55',
            useIgraRoom && 'overflow-visible',
          )}
        >
          <div
            className={cn(
              'relative h-full min-h-0 w-full',
              useIgraRoom && 'ab-game-arena-perspective',
            )}
          >
            <div
              className={cn(
                'relative h-full min-h-0 w-full',
                useIgraRoom && 'ab-game-arena-plane',
              )}
            >
              {useIgraRoom ? (
                <div className="ab-game-felt-wash rounded-[100px]" aria-hidden />
              ) : null}
              <div className="absolute inset-0 z-[2] flex items-center justify-center py-0.5">
                <div className="relative w-full max-w-lg md:max-w-xl">
                  <PokerTableSurface
                    ref={deckRef}
                    feltVariant={useIgraRoom ? 'photo' : 'classic'}
                    potAnchorRef={potAnchorRef}
                    communitySlots={[null, null, null, null, null]}
                    pot={potTricksTaken}
                    potLabel="ВЗЯТКИ"
                    showCommunity={false}
                    showDeck
                    deckPulseKey={engineState.roundIndex}
                    deckLayerExtra={
                      <BelotDeckTrumpIndicator
                        enabled
                        phase={engineState.phase}
                        contract={engineState.contract}
                        roundIndex={Number(engineState.roundIndex) || 0}
                        auctionRevealMs={deckTrumpRevealMs}
                        prefersReducedMotion={prefersReducedMotionDeckTrump}
                        cardIdToPlaying={cardIdToPlaying}
                      />
                    }
                  />
                </div>
              </div>

              <div className="pointer-events-none absolute inset-0" style={{ zIndex: BELOT_FX_Z_TABLE }}>
                {seatSlots.map((slot) => {
              const seatIndex = slot.seat;
              const disp = seatDisplayPercents(slot, seatOffsetBySeat[seatIndex]);
              const nm = seatDisplayName(seatIndex, heroName);
              const isHero = seatIndex === HUMAN_SEAT;
              const seatTurn =
                !roundDealAnimActive &&
                (engineState.phase === 'auction' ||
                  engineState.phase === 'declare' ||
                  engineState.phase === 'play') &&
                engineState.currentTurnSeat === seatIndex;
              const botTurnFraction =
                seatTurn && !isHero && oppTurnClock && oppTurnClock.seat === seatIndex
                  ? Math.max(0, Math.min(1, (oppTurnClock.endAt - Date.now()) / oppTurnClock.totalMs))
                  : undefined;
              const isMyTeamSeat = teamOfSeat(seatIndex) === teamOfSeat(HUMAN_SEAT);
              const seatNameTeamColorClass = isMyTeamSeat ? 'text-emerald-300' : 'text-rose-300';
              const mergedAuctionSeatAction = pickMergedAuctionSeatAction(
                seatIndex,
                auctionSeatActions || {},
                latestAuctionActionBySeat,
              );
              const regularSeatAction = lastSeatActions?.[seatIndex];
              const seatAction = inAuction ? mergedAuctionSeatAction : regularSeatAction;
              const isAuctionPassed = inAuction && normalizeAuctionKind(mergedAuctionSeatAction?.kind) === 'pass';
              const auctionKindUi = normalizeAuctionKind(seatAction?.kind);
              const showAuctionSpeech =
                inAuction &&
                auctionKindUi &&
                (auctionKindUi !== 'bid' || seatAction.bid);
              const nameActionBadge =
                !inAuction && seatAction?.kind && !showAuctionSpeech ? (
                  <SeatLastMoveBadge
                    key={`seat-action-${seatIndex}-${seatAction.at || seatAction.label || seatAction.kind}`}
                    action={seatAction}
                    compact={false}
                  />
                ) : null;
              const casualDeclareHand =
                engineState.phase === 'declare' &&
                String(engineState.declarationMode || 'casual') !== 'hardcore' &&
                Array.isArray(engineState.handsAtRoundStart?.[seatIndex])
                  ? engineState.handsAtRoundStart[seatIndex]
                  : null;
              const seatAuctionDropBadge = inAuction ? (
                <SeatAuctionDropBadge
                  key={`auction-drop-${seatIndex}-${seatAction?.at || seatAction?.label || seatAction?.kind || 'idle'}`}
                  action={seatAction}
                  isCurrentTurn={seatTurn}
                />
              ) : null;
              const phaseMatch = engineState.phase === 'declare' || engineState.phase === 'play';
              const stripKeys =
                Array.isArray(declaredRowsForSeatStrip) && declaredRowsForSeatStrip[seatIndex]
                  ? declaredRowsForSeatStrip[seatIndex]
                  : engineState.declaredComboKeysBySeat?.[seatIndex] ?? null;
              const seatTeamIx = teamOfSeat(seatIndex);
              const declarePills = collectDeclarePillLabels(
                stripKeys,
                engineState.phase,
                String(engineState.declarationMode || 'casual'),
                casualDeclareHand,
                {
                  battleOutcome: declarationBattleOutcome,
                  battleApply: declarationBattleApply,
                  seatTeamIndex: seatTeamIx,
                },
              );
              const comboJoined =
                declarePills.length > 0
                  ? declarePills.slice(0, 4).join(' · ') + (declarePills.length > 4 ? '…' : '')
                  : '';
              /** Текст «БЛОТ/РЕБЛОТ» только в речевом пузыре; капля — комбо без дубля. */
              const matchDropLabel = comboJoined || '';
              const seatMatchDropBadge =
                !inAuction && phaseMatch && matchDropLabel ? (
                  <SeatBelotMatchDropBadge
                    key={`match-drop-${seatIndex}-${belaSpeech?.token ?? 'x'}-${matchDropLabel}`}
                    label={matchDropLabel}
                    isCurrentTurn={seatTurn}
                    variant="combo"
                  />
                ) : null;
              const topModeIndicatorSlot = inAuction ? seatAuctionDropBadge : seatMatchDropBadge;
              const belaHere =
                belaSpeech && belaSpeech.seat === seatIndex ? (
                  <div
                    data-bela-bubble-root={belaSpeech.token}
                    className={cn(
                      'absolute left-1/2 -translate-x-1/2',
                      belaSpeech.tailUp ? 'top-full mt-px' : 'bottom-full mb-px',
                    )}
                    style={{
                      zIndex: BELOT_FX_Z_SPEECH_BUBBLE,
                      marginLeft: belaSpeech.towardCenterPx,
                    }}
                  >
                    <BelotFxBubble
                      variant="speech"
                      tail={belaSpeech.tailUp ? 'up' : 'down'}
                      speechTonePair={belotSpeechTonePairFromGlyphLine(belaSpeech.line2)}
                    >
                      <div className="flex flex-col items-center gap-px px-0.5">
                        <span
                          className={cn(
                            'select-none text-[10px] font-black uppercase tracking-wide leading-tight',
                            BELOT_FELT_LABEL_NEUTRAL,
                          )}
                        >
                          {belaSpeech.line1}
                        </span>
                        <div className="select-none text-[10px] font-black leading-none">
                          <BelotVividSuitInlineText
                            text={belaSpeech.line2}
                            neutralClassName={cn(BELOT_FELT_LABEL_NEUTRAL, 'text-[10px] font-black')}
                          />
                        </div>
                      </div>
                    </BelotFxBubble>
                  </div>
                ) : null;
              const voidHere =
                voidLeadCue && voidLeadCue.seat === seatIndex ? (
                  <div
                    className={cn(
                      'absolute left-1/2 z-[150] -translate-x-1/2',
                      belaSpeech?.seat === seatIndex && (belaSpeech.line1 === 'БЛОТ' || belaSpeech.line1 === 'РЕБЛОТ')
                        ? 'top-full mt-5'
                        : 'bottom-full mb-px',
                    )}
                  >
                    <div className="flex items-center gap-1 rounded-full border border-amber-400/45 bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-50 shadow-lg backdrop-blur-sm">
                      <Ban className="h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden />
                      <span>не в масть</span>
                      <span className="font-mono text-xs opacity-95">
                        {String(TRUMP_LABEL_RU[voidLeadCue.leadSuit] ?? '?')}
                      </span>
                    </div>
                  </div>
                ) : null;
              const belaBubbleAtThisSeat = Boolean(belaSpeech && belaSpeech.seat === seatIndex);
              const botEmoteHere =
                botEmoteCue && botEmoteCue.seat === seatIndex ? (
                  <div
                    key={`emote-${botEmoteCue.token}`}
                    className={cn(
                      'pointer-events-none absolute bottom-full left-1/2 z-[148] -translate-x-1/2',
                      belaBubbleAtThisSeat ? 'mb-10' : 'mb-px',
                    )}
                  >
                    <span className="text-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]" aria-hidden>
                      {botEmoteCue.emoji}
                    </span>
                  </div>
                ) : null;
              const shulerRingClass =
                shulerTableFx &&
                Array.isArray(shulerTableFx.seats) &&
                shulerTableFx.seats.includes(seatIndex)
                  ? shulerTableFx.kind === 'false_accuse'
                    ? 'rounded-2xl ring-4 ring-amber-400/90 ring-offset-[3px] ring-offset-black/60 shadow-[0_0_20px_rgba(251,191,36,0.35)]'
                    : 'rounded-2xl ring-4 ring-red-500/80 ring-offset-[3px] ring-offset-black/60 shadow-[0_0_22px_rgba(239,68,68,0.4)]'
                  : '';
              const voidShakeSeat =
                voidLeadCue && voidLeadCue.seat === seatIndex && !prefersReducedMotionBelotFx
                  ? 'motion-safe:animate-[poker-loss-shake_0.45s_ease-in-out_1] motion-reduce:animate-none'
                  : '';
              return (
                <div
                  key={`arm-seat-${seatIndex}`}
                  className="absolute"
                  style={{
                    left: `${disp.leftPct}%`,
                    top: `${disp.topPct}%`,
                    transform: igraEllipseSeatTransform(seatIndex, useIgraRoom, seatLayoutPreset),
                    transformOrigin: 'center',
                  }}
                >
                  <div className="w-max max-w-[100vw]">
                    <div
                      ref={(el) => {
                        seatNodeRefs.current[seatIndex] = el;
                        if (isHero && heroSeatAnchorRef) {
                          if (typeof heroSeatAnchorRef === 'function') heroSeatAnchorRef(el);
                          else heroSeatAnchorRef.current = el;
                        }
                      }}
                      className={cn(
                        'relative flex w-max max-w-[100vw] flex-col items-center gap-0.5 transition-all duration-250',
                        seatArrangeMode ? 'pointer-events-auto cursor-grab touch-none active:cursor-grabbing' : 'pointer-events-none',
                        isAuctionPassed && 'opacity-60 saturate-75',
                        voidShakeSeat,
                      )}
                      onPointerDown={(ev) =>
                        handleSeatDragPointerDown?.(ev, seatIndex, slot.leftPct, slot.topPct)
                      }
                      onPointerMove={handleSeatDragPointerMove}
                      onPointerUp={handleSeatDragPointerUp}
                      onPointerCancel={handleSeatDragPointerUp}
                      onLostPointerCapture={handleSeatDragLostCapture}
                    >
                    {botEmoteHere}
                    {voidHere}
                    {belaHere}
                    <div className={cn('rounded-2xl', shulerRingClass)}>
                    <IndustrialSeat
                      name={nm.length > 10 ? `${nm.slice(0, 9)}…` : nm}
                      stackMetaVertically
                      boostSeatAvatar
                      chipsDisplay={null}
                      chips={null}
                      nameColorClass={seatNameTeamColorClass}
                      isCurrentTurn={seatTurn}
                      isUser={isHero}
                      isBot={!isHero}
                      showCards={false}
                      cards={[]}
                      turnTimeLeftSec={isHero && seatTurn ? heroSecLeft : null}
                      turnTimeTotalSec={isHero && seatTurn ? HERO_TURN_SECONDS : null}
                      turnTimeFraction={botTurnFraction}
                      modeIndicatorSlot={topModeIndicatorSlot}
                      nameActionBadge={nameActionBadge}
                      suppressAvatarDragPropagation={seatArrangeMode}
                    />
                    </div>
                    <BelotSeatSignalStrip
                      phase={engineState.phase}
                      declarationMode={String(engineState.declarationMode || 'casual')}
                      declaredComboKeys={stripKeys}
                      casualDeclareHand={casualDeclareHand}
                      declarationBattleOutcome={declarationBattleOutcome}
                      declarationBattleApply={declarationBattleApply}
                      seatTeamIndex={seatTeamIx}
                    />
                    </div>
                  </div>
                </div>
              );
                })}
              </div>

              <BelotOnlineRevealOverlay
                comboFx={comboRevealFx}
                fx={null}
                embedded
                zIndex={BELOT_FX_Z_COMBO_FAN}
              />

              {showTableAuctionRiver ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-[14%] z-[12] flex justify-center px-2 sm:bottom-[16%]">
                  <div className="pointer-events-auto w-full max-w-[min(100%,22rem)] rounded-xl border border-white/12 bg-black/60 px-2 py-1.5 shadow-lg backdrop-blur-md sm:px-3 sm:py-2">
                    <BelotAuctionMovesRiver
                      variant="table"
                      auctionLog={auctionLog}
                      seatDisplayName={seatDisplayName}
                      heroName={heroName}
                      onOpenFullLog={() => setAuctionLogOpen(true)}
                    />
                  </div>
                </div>
              ) : null}

              <div
                ref={(el) => {
                  if (trickCenterRef) {
                    if (typeof trickCenterRef === 'function') trickCenterRef(el);
                    else trickCenterRef.current = el;
                  }
                  if (tableRevealAnchorRef) {
                    if (typeof tableRevealAnchorRef === 'function') tableRevealAnchorRef(el);
                    else tableRevealAnchorRef.current = el;
                  }
                }}
                className={cn(
                  'pointer-events-none absolute left-1/2 top-1/2 z-[19] -translate-x-1/2 -translate-y-1/2',
                  useIgraRoom
                    ? 'h-[min(52%,22rem)] w-[min(30%,10.5rem)] max-h-[min(46dvh,24rem)] max-w-[11rem] rounded-[50%] sm:h-[min(48%,23rem)] sm:max-h-[min(44dvh,26rem)] sm:w-[min(28%,11rem)] sm:max-w-[12rem]'
                    : 'h-[28%] w-[32%]',
                )}
                aria-hidden
              />
              {isHeroCardDragging ? (
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-[19] -translate-x-1/2 -translate-y-1/2">
                  <div
                    className={cn(
                      'shrink-0 rounded-[50%] border-2 transition-all duration-100',
                      useIgraRoom
                        ? 'h-[min(11.5rem,46vmin)] w-[min(5.75rem,24vmin)] max-h-[48vmin] max-w-[26vmin] sm:h-[min(12rem,44vmin)] sm:w-[min(6rem,22vmin)]'
                        : 'h-[10.8rem] w-24 max-h-[66vmin] max-w-[48vmin]',
                      isHeroCardDropActive
                        ? 'border-cyan-300/95 bg-cyan-500/18 shadow-[0_0_28px_rgba(34,211,238,0.5)]'
                        : 'border-cyan-400/55 bg-cyan-500/10 shadow-[0_0_18px_rgba(34,211,238,0.26)]',
                    )}
                  />
                </div>
              ) : null}

              {(() => {
                const phaseAllowsTableTrickCards = engineState.phase === 'play' || engineState.phase === 'dealing';
                if (!phaseAllowsTableTrickCards) return null;
                const overlayRows =
                  trickAnim && trickAnim.plays?.length ? playsToTrickUiRows(trickAnim.plays, heroName) : trickUi;
                if (!overlayRows.length) return null;
                return (
                  <div className="pointer-events-none absolute inset-0 z-20">
                    {overlayRows.map((c, idx) => {
                      const position = getRelativeSeatPosition(c.seat, HUMAN_SEAT);
                      const isWinningReveal =
                        Boolean(trickAnim) && trickAnim.phase === 'reveal' && trickAnim.winnerSeat === c.seat;
                      return (
                        <div
                          key={`${c.key}-ov-${trickAnim?.token ?? 'live'}-${idx}`}
                          ref={(el) => {
                            trickOverlayCardRefs.current[idx] = el;
                          }}
                          className="absolute left-1/2 top-1/2"
                          style={{ transform: TRICK_SLOT_TRANSFORM[position], zIndex: 10 + idx }}
                        >
                          <div
                            className={cn(
                              'relative',
                              Boolean(engineState.shulerEnabled) &&
                                Boolean(c.dirty) &&
                                'pointer-events-auto cursor-pointer',
                            )}
                            onDoubleClick={() => {
                              if (
                                !engineState.shulerEnabled ||
                                !c.dirty ||
                                typeof onAccuseDirtyPlay !== 'function' ||
                                !Number.isInteger(c.playCardId)
                              ) {
                                return;
                              }
                              onAccuseDirtyPlay(c.playCardId);
                            }}
                            title={
                              Boolean(engineState.shulerEnabled) && c.dirty
                                ? 'Двойной клик — обвинить в подмене карты'
                                : undefined
                            }
                          >
                            <PlayingCard
                              rank={c.rank}
                              suit={c.suit}
                              size="sm"
                              className={cn(
                                'shadow-2xl',
                                isWinningReveal && 'ring-2 ring-neon-gold ring-offset-1 ring-offset-black/35',
                                Boolean(c.dirty) && 'ring-2 ring-red-500/90 ring-offset-1 ring-offset-black/40',
                              )}
                            />
                            <span className="absolute -bottom-4 left-1/2 max-w-[5rem] -translate-x-1/2 truncate whitespace-nowrap rounded bg-black/55 px-1 py-0.5 text-[9px] text-white/80">
                              {c.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {inAuction && auctionLogOpen ? (
          <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/45 p-3 backdrop-blur-[2px] sm:items-center">
            <div className="w-full max-w-md rounded-xl border border-border/65 bg-[#0e1523]/96 p-3 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] font-black uppercase tracking-wide text-amber-100">Лог торгов</p>
                <button
                  type="button"
                  onClick={() => setAuctionLogOpen(false)}
                  className="rounded-md border border-border/50 px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted/20 hover:text-foreground"
                >
                  Закрыть
                </button>
              </div>
              <div className="max-h-[44vh] space-y-1.5 overflow-y-auto pr-1">
                {Array.isArray(auctionLog) && auctionLog.length > 0 ? (
                  auctionLog.map((row, idx) => (
                    <div
                      key={row.id || `auction-log-${idx}`}
                      className="rounded-md border border-border/45 bg-black/25 px-2 py-1.5 text-[11px] animate-[app-toast-in_260ms_cubic-bezier(0.2,0.8,0.2,1)]"
                    >
                      <span className="font-semibold text-slate-100">{seatDisplayName(row.seat, heroName)}</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-amber-100">{row.label}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[11px] text-muted-foreground">Пока без записей</p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {heroAuctionPanel ? (
        <div
          className={cn(
            'z-20 shrink-0 border-t border-border/40 bg-background/95 px-1.5 pt-1 sm:px-2 sm:pt-1.5',
            showHeroHandRow && 'border-b border-border/25 pb-1',
          )}
        >
          {heroAuctionPanel}
        </div>
      ) : null}

      {showHeroHandRow ? (
        <div
          className={cn(
            'pointer-events-none relative z-0 shrink-0 border-t-0 bg-[transparent] px-2 pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-0.5 shadow-none sm:px-3 sm:pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pt-1',
            '[&_button]:pointer-events-auto',
            useIgraRoom && heroAuctionPanel && 'pt-0 sm:pt-0.5',
          )}
        >
          <div
            className={cn(
              'mx-auto rounded-xl border border-transparent bg-[transparent] shadow-none',
              seatLayoutPreset === 'mobile-stack' ? 'p-1 sm:p-1.5' : 'p-1.5 sm:p-2',
              useIgraRoom && heroAuctionPanel && (seatLayoutPreset === 'mobile-stack' ? 'p-0.5 sm:p-1' : 'p-1 sm:p-1.5'),
            )}
          >
            <div
              className={cn(
                'flex min-h-0 items-stretch justify-center overflow-hidden pb-0',
                useIgraRoom && heroAuctionPanel ? 'pt-0.5 sm:pt-1' : 'pt-1 sm:pt-2',
                seatLayoutPreset === 'mobile-stack'
                  ? useIgraRoom && heroAuctionPanel
                    ? 'h-[148px] sm:h-[162px] md:h-[172px]'
                    : 'h-[198px] sm:h-[220px] md:h-[236px]'
                  : useIgraRoom && heroAuctionPanel
                    ? 'h-[118px] sm:h-[128px] md:h-[136px]'
                    : 'h-[168px] sm:h-[184px] md:h-[196px]',
              )}
            >
              <div
                className="relative h-full min-h-0 min-w-0"
                style={{ width: `${handFanMetrics.fanWidth}px` }}
              >
                {showHandPlayGoldEllipse ? (
                  <div
                    className={cn(
                      'pointer-events-none absolute left-1/2 bottom-0 z-[1] -translate-x-1/2 translate-y-1 rounded-[50%] border-2 border-neon-gold/50 bg-gradient-to-t from-neon-gold/14 via-neon-gold/5 to-transparent shadow-[0_0_32px_rgba(255,215,0,0.22)]',
                      seatLayoutPreset === 'mobile-stack'
                        ? 'h-[6.5rem] w-[min(100%,17rem)] max-w-[94%]'
                        : 'h-[7.35rem] w-[min(100%,19rem)] max-w-[92%]',
                    )}
                    aria-hidden
                  />
                ) : null}
                {humanHandSorted.map((cardId, idx) => {
                  const { handCount, fanStep, fanBaseYOffset } = handFanMetrics;
                  const compactFan = seatLayoutPreset === 'mobile-stack';
                  const centerOffset = idx - (handCount - 1) / 2;
                  const spreadFactor = handCount > 1 ? centerOffset / ((handCount - 1) / 2) : 0;
                  const rotate = spreadFactor * (compactFan ? 14 : 18);
                  const curveY = Math.abs(spreadFactor) * (compactFan ? 14 : 20);
                  const translateX = centerOffset * fanStep;
                  const tf = `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY}px) rotate(${rotate}deg)`;
                  const heroRevealRow = seatDealSlotRevealed?.[HUMAN_SEAT];
                  if (roundDealAnimActive && humanHandSorted.length === 8) {
                    const slotRevealed = Boolean(heroRevealRow?.[idx]);
                    return (
                      <div
                        key={`deal-slot-${idx}`}
                        ref={handSlotRefs[idx]}
                        className="absolute bottom-0 left-1/2"
                        style={{ zIndex: idx + 1, transform: tf }}
                        aria-hidden
                      >
                        {slotRevealed ? (
                          <div className="pointer-events-none">
                            <PlayingCard faceDown size="md" />
                          </div>
                        ) : (
                          <div className="pointer-events-none h-[5.5rem] w-16 shrink-0 opacity-0" />
                        )}
                      </div>
                    );
                  }
                  const { rank, suit } = cardIdToPlaying(cardId);
                  const canPlay =
                    !isInputBlocked &&
                    isHumanPlayTurn &&
                    legalCardIds.has(cardId) &&
                    !humanFirstPlayCommitPending;
                  const inAuctionPhase = engineState.phase === 'auction';
                  const liftY =
                    hoveredCardIndex === idx && canPlay ? (compactFan ? 16 : 22) : 0;
                  const baseScale = canPlay ? (compactFan ? 1.1 : 1.2) : 1;
                  const scale =
                    hoveredCardIndex === idx && canPlay
                      ? compactFan
                        ? 1.14
                        : 1.24
                      : baseScale;
                  const baseZIndex = canPlay ? 80 + idx : idx + 1;
                  const isDraggedOriginCard =
                    isHeroCardDragging &&
                    draggingCardId === cardId &&
                    draggingCardOriginIndex === idx;
                  const isPendingHiddenCard = pendingHiddenCardId === cardId;
                  return (
                    <button
                      key={`${cardId}-${idx}`}
                      ref={handSlotRefs[idx]}
                      type="button"
                      onClick={() => canPlay && onPlayCard(cardId)}
                      onPointerDown={(ev) => onHeroCardPointerDown?.(ev, cardId, idx, canPlay)}
                      onPointerMove={onHeroCardPointerMove}
                      onPointerUp={onHeroCardPointerUp}
                      onPointerCancel={onHeroCardPointerCancel}
                      onMouseEnter={() => setHoveredCardIndex(idx)}
                      onMouseLeave={() => setHoveredCardIndex(null)}
                      onFocus={() => setHoveredCardIndex(idx)}
                      onBlur={() => setHoveredCardIndex(null)}
                      disabled={!canPlay}
                      className={cn(
                        'absolute bottom-0 left-1/2 appearance-none border-0 bg-transparent p-0 transition-[transform,filter] focus:outline-none touch-none select-none',
                        isHeroCardDragging ? 'duration-0' : 'duration-300',
                        canPlay
                          ? 'cursor-pointer'
                          : inAuctionPhase
                            ? 'cursor-not-allowed'
                            : 'cursor-not-allowed opacity-65',
                      )}
                      style={{
                        zIndex: hoveredCardIndex === idx ? 200 : baseZIndex,
                        transform: `translateX(calc(-50% + ${translateX}px)) translateY(${fanBaseYOffset + curveY - liftY}px) rotate(${rotate}deg) scale(${scale})`,
                      }}
                    >
                      {isDraggedOriginCard || isPendingHiddenCard ? (
                        <div className="h-[5.5rem] w-16 rounded-md border border-cyan-300/25 bg-cyan-500/10 opacity-35" />
                      ) : (
                        <PlayingCard
                          suit={suit}
                          rank={rank}
                          size="md"
                          highlighted={canPlay}
                          className={cn(
                            canPlay &&
                              'ring-1 ring-neon-gold/55 shadow-[0_6px_22px_rgba(255,215,0,0.35)]',
                            !canPlay && 'grayscale-[0.1]',
                          )}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
}

